import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigurationService, UserListDto } from '../configuration.service';
import { AuthService } from '../../services/auth.service';
import { UserEditDialogComponent } from './user-edit-dialog.component';
import { UserRoleAssignDialogComponent } from './user-role-assign-dialog.component';

export interface UserDetailDialogData {
  user: UserListDto;
}

@Component({
  selector: 'app-user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['../configuration-form.css', './user-detail.component.css'],
})
export class UserDetailComponent implements OnInit {
  user: UserListDto;
  // role name -> id, so we can remove a role the list only carries by name.
  private roleIdByName: Record<string, number> = {};
  // Tracks whether any change happened, so the list reloads on close.
  private changed = false;
  resettingPassword = false;

  constructor(
    private dialogRef: MatDialogRef<UserDetailComponent>,
    @Inject(MAT_DIALOG_DATA) private data: UserDetailDialogData,
    private api: ConfigurationService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    public auth: AuthService
  ) {
    // Clone so edits here don't mutate the row behind the list before reload.
    this.user = { ...data.user, roles: [...data.user.roles] };
  }

  /**
   * Assigning and revoking a role needs BOTH keys, because the server needs
   * both: UsersController carries two RequiresPermission attributes on each of
   * those routes, and ASP.NET ANDs them. Gating on User.Manage alone offered
   * the affordance to a role that could only ever get a 403 back, reported as
   * "Failed to remove role" with no hint that it was a permissions problem.
   */
  get canManageRoles(): boolean {
    return this.auth.hasPermission('User.Manage')
        && this.auth.hasPermission('Role.Manage');
  }

  ngOnInit(): void {
    this.api.getRoles().subscribe({
      next: roles => roles.forEach(r => this.roleIdByName[r.name] = r.id),
    });
  }

  openEdit(): void {
    const ref = this.dialog.open(UserEditDialogComponent, {
      width: '520px', maxWidth: '92vw', data: this.user,
    });
    ref.afterClosed().subscribe(result => {
      if (result?.updated) { this.changed = true; this.close(); }
    });
  }

  openAssignRole(): void {
    const assignedRoleIds = this.user.roles
      .map(name => this.roleIdByName[name])
      .filter((id): id is number => id != null);

    const ref = this.dialog.open(UserRoleAssignDialogComponent, {
      width: '400px', maxWidth: '92vw',
      data: { userId: this.user.id, assignedRoleIds },
    });
    ref.afterClosed().subscribe(result => {
      if (result?.assigned) { this.changed = true; this.close(); }
    });
  }

  confirmPasswordReset(): void {
    if (this.resettingPassword) return;
    const label = this.user.displayName || this.user.userName || this.user.emailAddress;
    if (!confirm(`Send a password reset email to ${label}?`)) return;

    this.resettingPassword = true;
    this.api.sendPasswordReset(this.user.id).subscribe({
      next: () => {
        this.resettingPassword = false;
        this.snackBar.open('Password reset email sent', undefined, { duration: 4000 });
      },
      error: err => {
        this.resettingPassword = false;
        this.snackBar.open(
          err.error?.message || 'Failed to send password reset email',
          undefined,
          { duration: 4000 });
      },
    });
  }

  confirmRemoveRole(roleName: string): void {
    const roleId = this.roleIdByName[roleName];
    if (roleId == null) {
      this.snackBar.open('Cannot resolve role; reopen and try again', undefined, { duration: 4000 });
      return;
    }
    if (!confirm(`Remove ${roleName} from this user?`)) return;
    this.api.removeRole(this.user.id, roleId).subscribe({
      next: () => {
        this.user.roles = this.user.roles.filter(r => r !== roleName);
        this.changed = true;
      },
      error: () => this.snackBar.open('Failed to remove role', undefined, { duration: 4000 }),
    });
  }

  back(): void {
    this.close();
  }

  private close(): void {
    this.dialogRef.close(this.changed ? { changed: true } : undefined);
  }
}
